import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ArrowRight, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/**
 * Resident-facing landing page that finishes off any Stripe autopilot
 * charge that needs 3DS confirmation. Loads Stripe.js lazily and uses
 * `stripe.confirmCardPayment(client_secret)` to drive the challenge in a
 * modal.
 */
export default function Pending3DSPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [stripe, setStripe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/autopilot/needs-action");
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lazy-load Stripe.js from CDN
  useEffect(() => {
    if (window.Stripe) return;
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3";
    s.async = true;
    document.head.appendChild(s);
  }, []);

  const confirm3DS = async (p) => {
    setBusyId(p.id);
    try {
      const { data } = await api.post(`/me/autopilot/needs-action/${p.id}/prepare`);
      if (!window.Stripe) {
        toast.error("Stripe.js didn't load — refresh the page and try again.");
        setBusyId(null);
        return;
      }
      const s = stripe || window.Stripe(data.publishable_key);
      if (!stripe) setStripe(s);

      // `handleCardPayment` (v2) / `confirmCardPayment` (v3) walks the resident
      // through the 3DS challenge using the client_secret we retrieved server-side.
      const result = await s.confirmCardPayment(data.client_secret);
      if (result.error) {
        toast.error(result.error.message || "3DS challenge failed");
        // Ask the server to re-check the intent anyway — it may have advanced state
        await api.post(`/me/autopilot/needs-action/${p.id}/complete`);
        await load();
      } else if (result.paymentIntent?.status === "succeeded") {
        // Have the server confirm + update the ledger
        await api.post(`/me/autopilot/needs-action/${p.id}/complete`);
        toast.success(`${money(p.amount)} confirmed — thanks for finishing that up`);
        await load();
      } else {
        toast.warning(`Status: ${result.paymentIntent?.status || "unknown"}`);
        await load();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Confirmation failed");
    }
    setBusyId(null);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Autopilot"
        title="Finish up 3DS charges"
        subtitle="Your bank paused these autopilot charges for extra confirmation. Finish them here and they'll clear right away — the amount + card on file are unchanged."
      />

      {loading ? (
        <Card className="p-10 text-center border-white/10">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-zinc-500" />
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-white/10" data-testid="pending3ds-empty">
          <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-400 mb-2" />
          <div className="text-sm text-zinc-400">No pending 3DS charges — you&apos;re all clear.</div>
          <Button onClick={() => navigate("/resident/profile")}
                  variant="outline" className="mt-3 border-white/10"
                  data-testid="pending3ds-back">
            Back to profile
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <Card key={p.id} className="p-5 border-amber-500/30 bg-amber-500/[0.03]"
                  data-testid={`pending3ds-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className="h-4 w-4 text-amber-400" />
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">
                      needs 3DS
                    </Badge>
                    <span className="text-[11px] text-zinc-500">
                      {new Date(p.auto_paid_at || p.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-lg font-display font-semibold text-zinc-100 tabular-nums">{money(p.amount)}</div>
                  <div className="text-xs text-zinc-400">{p.memo || p.type || "Autopilot charge"}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 font-mono">Intent · {p.stripe_payment_intent_id?.slice(0, 22)}…</div>
                </div>
                <Button
                  onClick={() => confirm3DS(p)}
                  disabled={busyId === p.id}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-full shrink-0"
                  data-testid={`pending3ds-confirm-${p.id}`}
                >
                  {busyId === p.id ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Confirming…</>
                  ) : (
                    <>Finish confirmation <ArrowRight className="h-3 w-3 ml-1" /></>
                  )}
                </Button>
              </div>
            </Card>
          ))}
          <div className="text-[11px] text-zinc-500 text-center mt-2">
            <XCircle className="h-3 w-3 inline mr-1" /> If a charge stays stuck, remove your card and add a new one from your profile.
          </div>
        </div>
      )}
    </div>
  );
}
